import React from 'react';
import classnames from 'classnames';

import {
    getFirstNodeWithCheckPassing,
    getValueOfFirstNodeWithCheckPassing,
    formatNumberForDisplay,
} from './SearchEventCardDebugInfoUtils';

const checkIfMaxOrSum = (description) =>
    description === 'max of:' || description === 'sum of:';
const checkIfTermWeight = (description) => description.startsWith('weight(');
const getReactKey = (level, index) => `search-debug-key-${level}-${index}`;

// There are many levels of the tree that aren't useful to render
const checkfIfLevelRedundant = (detail, relevanceScore) => {
    if (checkIfMaxOrSum(detail.description)) {
        if (
            detail.details.length === 1 &&
            checkIfMaxOrSum(detail.details[0].description)
        ) {
            return true;
        }

        if (detail.value === relevanceScore.value) {
            return true;
        }
    }

    if (!detail.value) {
        return true;
    }

    return false;
};

const MaxOrSumComponent = ({ detail, relevanceScore, recurse }) => {
    const shouldRenderComponent = !checkfIfLevelRedundant(
        detail,
        relevanceScore,
    );
    const label = detail.description;
    const value = detail.value;

    return (
        <div className="eds-l-mar-hor-1">
            {shouldRenderComponent && (
                <div
                    className={`${
                        shouldRenderComponent
                            ? 'search-event-card-debug-info__relevance-column--should-underline eds-l-mar-top-1'
                            : ''
                    }`}
                >
                    {value && <div>{Number(value).toPrecision(4)}</div>}
                </div>
            )}
            {shouldRenderComponent && <div>{label}</div>}
            <div
                className={classnames({
                    'search-event-card-debug-info__relevance-row': shouldRenderComponent,
                })}
            >
                {recurse(detail.details, detail, relevanceScore)}
            </div>
        </div>
    );
};

const TermWeightComponent = ({ detail }) => {
    const termFreq = getValueOfFirstNodeWithCheckPassing(
        detail.details,
        (detail) => detail.description.startsWith('termFreq'),
    );
    const idf = getValueOfFirstNodeWithCheckPassing(detail.details, (detail) =>
        detail.description.startsWith('idf'),
    );
    const label = detail.description.split('weight(')[1].split(' in')[0];
    const value = detail.value;

    return (
        <div className="eds-l-mar-hor-1">
            <div className="search-event-card-debug-info__relevance-column--should-underline eds-l-mar-top-1">
                <div>{formatNumberForDisplay(value)}</div>
            </div>
            <div className="eds-text-weight--heavy">{label}</div>
            <div>termFreq: {termFreq}</div>
            <div>idf: {formatNumberForDisplay(idf)}</div>
        </div>
    );
};

const renderDetail = ({ detail, relevanceScore, recurse, reactKey }) => {
    const isTermWeight = checkIfTermWeight(detail.description);

    let Component = MaxOrSumComponent;

    if (isTermWeight) {
        Component = TermWeightComponent;
    }

    return (
        <Component
            key={reactKey}
            detail={detail}
            relevanceScore={relevanceScore}
            recurse={recurse}
        />
    );
};

const extractTermsFromRelevanceScore = (
    details,
    parentDetail,
    relevanceScore,
    level = 0,
) =>
    details.map((detail, index) =>
        renderDetail({
            detail,
            relevanceScore,
            reactKey: getReactKey(level, index),
            recurse: (details, parentDetail, relevanceScore) =>
                extractTermsFromRelevanceScore(
                    details,
                    parentDetail,
                    relevanceScore,
                    level + 1,
                ),
        }),
    );

export const checkIfNodeIsRelevanceScore = (detail, parentDetail) => {
    if (!detail || !detail.details.length) {
        return false;
    }

    if (
        parentDetail &&
        parentDetail.description === 'function score, product of:'
    ) {
        return parentDetail.details[0] === detail;
    }

    return false;
};

export const getRelevanceComponents = (esExplainPlan) => {
    // The relevance score also includes "required clauses" like enforcing a lat/long radius
    // and an appropriate end date. Ignore those since they are filters and more like booleans,
    // they currently don't produce interesting information for debug purposes.
    const relevanceScoreIncludingRequiredClauses = getFirstNodeWithCheckPassing(
        [esExplainPlan],
        checkIfNodeIsRelevanceScore,
    );

    // If they don't do a "q" search, there is no relevance score
    if (relevanceScoreIncludingRequiredClauses) {
        const relevanceScore =
            relevanceScoreIncludingRequiredClauses.details[0];

        return (
            <div className="search-event-card-debug-info__relevance-components-container">
                {extractTermsFromRelevanceScore(
                    relevanceScore.details,
                    relevanceScore,
                    relevanceScore.value,
                )}
            </div>
        );
    }
};

const SearchEventCardDebugRelevanceInfo = ({ debugInfo }) => (
    <div className="search-event-card-debug-info__relevance">
        <div className="eds-text-weight--heavy">Relevance breakdown</div>
        {getRelevanceComponents(debugInfo.esExplainPlan)}
    </div>
);

export default SearchEventCardDebugRelevanceInfo;
